/**
 * SEC-003 — Helper pour authentifier les WebSocket post-migration cookies HttpOnly.
 *
 * Pourquoi un ticket ?
 * Les WebSocket en navigateur n'envoient pas systématiquement les cookies
 * (SameSite/CORS imprévisible cross-origin). On obtient donc un JWT court-vie
 * via /api/auth/ws-ticket (auth via cookie HttpOnly ou Bearer), puis on le
 * passe en query-string sur l'URL WS.
 *
 * - TTL serveur : 60 s
 * - Cache local : 45 s (on rafraîchit avant expiration)
 * - Aucune persistance localStorage : le ticket reste en mémoire process.
 */
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

let cachedTicket = null;
let cachedExpiry = 0;
let inflight = null;

const CACHE_MARGIN_MS = 15_000; // on jette le ticket 15 s avant la fin TTL

/**
 * Retourne un ticket frais (depuis le cache si encore valide, sinon récupère).
 * Renvoie `null` si l'utilisateur n'est pas authentifié (401).
 */
export async function getWsTicket() {
  const now = Date.now();
  if (cachedTicket && now < cachedExpiry - CACHE_MARGIN_MS) {
    return cachedTicket;
  }
  if (inflight) return inflight;

  inflight = (async () => {
    try {
      const resp = await axios.get(`${API_URL}/api/auth/ws-ticket`, { withCredentials: true });
      const { ticket, expiresIn } = resp.data || {};
      if (!ticket) return null;
      cachedTicket = ticket;
      cachedExpiry = Date.now() + (Number(expiresIn) || 60) * 1000;
      return ticket;
    } catch (_) {
      cachedTicket = null;
      cachedExpiry = 0;
      return null;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

/**
 * Invalide manuellement le ticket en cache (sur logout par ex.).
 */
export function clearWsTicket() {
  cachedTicket = null;
  cachedExpiry = 0;
}

/**
 * Construit une URL WS authentifiée prête à être passée à `new WebSocket(...)`.
 * Renvoie `null` si pas de ticket (caller doit gérer le no-op).
 *
 * @param {string} path - ex. "/ws/admin/tracking" (sans préfixe /api)
 */
export async function buildAuthenticatedWsUrl(path) {
  const ticket = await getWsTicket();
  if (!ticket) return null;
  const wsProtocol = API_URL.startsWith('https') ? 'wss' : 'ws';
  const baseUrl = API_URL.replace(/^https?/, wsProtocol);
  const sep = path.includes('?') ? '&' : '?';
  return `${baseUrl}/api${path}${sep}token=${encodeURIComponent(ticket)}`;
}
